import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Mapped, Silent } from "@/__main__/data-model.mjs";
import { QUEUE_SYMBOLS } from "@/game-lol/constants/constants-queue-symbols.mjs";
// import { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
// import { ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";
import QueueSymbol from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const ChampionResult = {
  patch: String,
  champion_id: Number,
  championId: Number,
  queue_id: QueueSymbol,
  tier: RankSymbol,
  individual_position: RoleSymbol,
  region: String,
  games: Number,
  win_rate: Number,
  role_pct_games: Number,
  is_most_popular_role: Boolean,
  champion_role_tier: Number,
  champion_role_tier_filtered: Number,
  champion_role_pickrate: Number,
  ban_num_games: Number,
  total_num_games: Number,
  champion_ban_rate: Number,
  role_total_num_games: Number,
  profiling: Mapped({
    key: String,
    value: Number,
  }),
  postmatch_stats: Mapped({
    key: String,
    value: Number,
  }),
  strong_against: Silent([Number]),
  weak_against: Silent([Number]),

  previous_patch_stats: Silent(
    Mapped({
      key: String,
      value: Number,
    }),
  ),

  filters: String,
} satisfies ModelDefinition;

const ChampionsModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [[String]],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([ChampionResult], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const champions = list.map((c) => {
    const result = {
      patch: "",
      queue_id: null,
      champion_id: 0,
      championId: 0,
      tier: null,
      champion_role_tier_filtered: 0,
      champion_role_pickrate: 0,
      individual_position: null,
      region: "",
      games: 0,
      win_rate: 0,
      role_pct_games: 0,
      is_most_popular_role: false,
      champion_role_tier: 0,
      ban_num_games: 0,
      total_num_games: 0,
      champion_ban_rate: 0,
      role_total_num_games: 0,
      postmatch_stats: {},
      profiling: {},
      strong_against: [],
      weak_against: [],
      previous_patch_stats: {},
      filters: "",
    };
    for (const column of columns) {
      if (!result.hasOwnProperty(column.name)) continue;
      const value: string = (c[column.position] || "") as unknown as string;
      result[column.name] = castType(column.typeName, value);
    }

    result.championId = result.champion_id;

    // Choosing to override the default tier with a better one rather than a ton of find and replace
    result.champion_role_tier =
      QueueSymbol(result.queue_id) === QUEUE_SYMBOLS.aram
        ? result.champion_role_tier
        : result.champion_role_tier_filtered;

    return result;
  });

  return champions;
});

export default ChampionsModel;

export type ChampionsStats = FromModel<typeof ChampionsModel>;
export type ChampionStats = ChampionsStats[number];

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "MAP":
      return value ? JSON.parse(value) : {};
    case "ARRAY":
      return value ? JSON.parse(value) : [];
    case "STRUCT":
      return value ? JSON.parse(value) : {};
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}
